import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Row,
  Col,
  Form,
  Alert,
  Spinner,
  Modal,
} from "react-bootstrap";
import { IconTrash, IconPlus } from "@tabler/icons-react";
import { UNIT_OPTIONS } from "../constants/units";
import { API_URL } from "utils/constants";
import { useAuth } from "hooks/useAuth";
import PermissionMiddleware from "middleware/PermissionMiddleware";

const InventoryForm = ({
  initialData,
  onSuccess,
  onError,
  categories = [],
  warehouses = [],
  show,
  onHide,
}) => {
  // Prepare category options from categories data
  const categoryOptions = categories.map((category) => ({
    value: category.id,
    label: category.name,
  }));

  const warehouseOptions = warehouses.map((warehouse) => ({
    value: warehouse.id,
    label: warehouse.name,
  }));

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    nama_alias: "",
    category_id: "",
    unit: "",
    description: "",
    current_stock: 0,
    minimum_stock: 0,
    price: 0,
    is_active: true,
  });

  // State for warehouse stocks
  const [warehouseStocks, setWarehouseStocks] = useState([
    {
      warehouse_id: "",
      stock: 0,
      minimum_stock: 0,
      note: "",
    },
  ]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const isUpdateMode = initialData && initialData.id;

  // Initialize form data
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || "",
        nama_alias: initialData.nama_alias || "",
        category_id: initialData.category_id || "",
        unit: initialData.unit || "",
        description: initialData.description || "",
        current_stock:
          initialData.current_stock || initialData.current_stock_total || 0,
        minimum_stock: initialData.minimum_stock || 0,
        price: initialData.price || 0,
        is_active: initialData.is_active !== undefined ? initialData.is_active : true,
      });

      if (
        initialData.warehouse_stocks &&
        initialData.warehouse_stocks.length > 0
      ) {
        setWarehouseStocks(initialData.warehouse_stocks);
      }
    }
  }, [initialData]);

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const addWarehouseStock = () => {
    setWarehouseStocks([
      ...warehouseStocks,
      {
        warehouse_id: "",
        stock: 0,
        minimum_stock: 0,
        note: "",
      },
    ]);
  };

  const removeWarehouseStock = (index) => {
    if (warehouseStocks.length > 1) {
      setWarehouseStocks(warehouseStocks.filter((_, i) => i !== index));
    }
  };

  const updateWarehouseStock = (index, field, value) => {
    const updated = warehouseStocks.map((stock, i) =>
      i === index ? { ...stock, [field]: value } : stock
    );
    setWarehouseStocks(updated);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Item name is required";
    }

    if (!formData.category_id) {
      newErrors.category_id = "Category is required";
    }

    if (!formData.unit) {
      newErrors.unit = "Unit is required";
    }

    if (formData.current_stock < 0) {
      newErrors.current_stock = "Current stock cannot be negative";
    }

    if (formData.minimum_stock < 0) {
      newErrors.minimum_stock = "Minimum stock cannot be negative";
    }

    // Validate warehouse stocks
    warehouseStocks.forEach((stock, index) => {
      if (!stock.warehouse_id) {
        newErrors[`warehouse_${index}`] = "Warehouse is required";
      }
      if (stock.stock < 0) {
        newErrors[`stock_${index}`] = "Stock cannot be negative";
      }
      if (stock.minimum_stock < 0) {
        newErrors[`min_stock_${index}`] = "Minimum stock cannot be negative";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSubmitError("");

    try {
      const payload = {
        ...formData,
        warehouse_stocks: warehouseStocks,
      };

      const url = isUpdateMode
        ? `${API_URL}/inventory/${initialData.id}`
        : `${API_URL}/inventory`;

      const response = await fetch(url, {
        method: isUpdateMode ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (onSuccess) {
        onSuccess(result);
      }

      if (onHide) {
        onHide();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      const errorMessage = error.message || "Failed to save inventory item";
      setSubmitError(errorMessage);

      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form data when closing
    setFormData({
      name: "",
      nama_alias: "",
      category_id: "",
      unit: "",
      description: "",
      current_stock: 0,
      minimum_stock: 0,
      price: 0,
      is_active: true,
    });
    setWarehouseStocks([
      {
        warehouse_id: "",
        stock: 0,
        minimum_stock: 0,
        note: "",
      },
    ]);
    setErrors({});
    setSubmitError("");

    if (onHide) {
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" className="workshop-modal-scope">
      <Modal.Header closeButton>
        <Modal.Title>
          {isUpdateMode ? "Edit" : "Create"} Inventory Item
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {submitError && (
            <Alert variant="danger" className="mb-3">
              {submitError}
            </Alert>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Item Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                  isInvalid={!!errors.name}
                  placeholder="Enter item name"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Alias Name</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.nama_alias}
                  onChange={(e) => handleFormChange("nama_alias", e.target.value)}
                  placeholder="Enter alias name (optional)"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Category *</Form.Label>
                <Form.Select
                  value={formData.category_id}
                  onChange={(e) =>
                    handleFormChange("category_id", e.target.value)
                  }
                  isInvalid={!!errors.category_id}
                >
                  <option value="">Select Category</option>
                  {categoryOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.category_id}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Unit *</Form.Label>
                <Form.Select
                  value={formData.unit}
                  onChange={(e) => handleFormChange("unit", e.target.value)}
                  isInvalid={!!errors.unit}
                >
                  <option value="">Select Unit</option>
                  {UNIT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.unit}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Current Stock *</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={formData.current_stock}
                  onChange={(e) =>
                    handleFormChange(
                      "current_stock",
                      parseInt(e.target.value) || 0
                    )
                  }
                  isInvalid={!!errors.current_stock}
                  placeholder="0"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.current_stock}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Minimum Stock *</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={formData.minimum_stock}
                  onChange={(e) =>
                    handleFormChange(
                      "minimum_stock",
                      parseInt(e.target.value) || 0
                    )
                  }
                  isInvalid={!!errors.minimum_stock}
                  placeholder="0"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.minimum_stock}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <PermissionMiddleware allowedPermissions={["see_price_inventory"]}>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Price (Rp)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={formData.price}
                    onChange={(e) =>
                      handleFormChange("price", parseFloat(e.target.value) || 0)
                    }
                    placeholder="0"
                  />
                  <Form.Text className="text-muted">
                    Harga beli atau harga satuan item.
                  </Form.Text>
                </Form.Group>
              </Col>
            </PermissionMiddleware>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.description}
                  onChange={(e) =>
                    handleFormChange("description", e.target.value)
                  }
                  placeholder="Enter item description"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="is-active-switch"
                  label={formData.is_active ? "Status: Aktif" : "Status: Tidak Aktif"}
                  checked={formData.is_active}
                  onChange={(e) => handleFormChange("is_active", e.target.checked)}
                />
                <Form.Text className="text-muted">
                  Item yang tidak aktif tidak akan muncul saat pencarian transaksi/pembuatan order.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Card className="mt-4">
            <Card.Header>
              <h6 className="mb-0">Warehouse Stocks</h6>
            </Card.Header>
            <Card.Body>
              {warehouseStocks.map((stock, index) => (
                <Card
                  key={index}
                  className="mb-3"
                  style={{ border: "1px solid #e5e7eb" }}
                >
                  <Card.Body>
                    <Row className="align-items-end">
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Warehouse *</Form.Label>
                          <Form.Select
                            value={stock.warehouse_id}
                            onChange={(e) =>
                              updateWarehouseStock(
                                index,
                                "warehouse_id",
                                e.target.value
                              )
                            }
                            isInvalid={!!errors[`warehouse_${index}`]}
                          >
                            <option value="">Select Warehouse</option>
                            {warehouseOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors[`warehouse_${index}`]}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label>Stock *</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            value={stock.stock}
                            onChange={(e) =>
                              updateWarehouseStock(
                                index,
                                "stock",
                                parseInt(e.target.value) || 0
                              )
                            }
                            isInvalid={!!errors[`stock_${index}`]}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors[`stock_${index}`]}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label>Min Stock *</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            value={stock.minimum_stock}
                            onChange={(e) =>
                              updateWarehouseStock(
                                index,
                                "minimum_stock",
                                parseInt(e.target.value) || 0
                              )
                            }
                            isInvalid={!!errors[`min_stock_${index}`]}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors[`min_stock_${index}`]}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Note</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Optional note"
                            value={stock.note}
                            onChange={(e) =>
                              updateWarehouseStock(
                                index,
                                "note",
                                e.target.value
                              )
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={1}>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeWarehouseStock(index)}
                          disabled={warehouseStocks.length === 1}
                        >
                          <IconTrash size={16} />
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
              <Button variant="outline-primary" onClick={addWarehouseStock}>
                <IconPlus size={16} className="me-2" />
                Add Warehouse Stock
              </Button>
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading && <Spinner size="sm" className="me-2" />}
            {isUpdateMode ? "Update" : "Create"} Item
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default InventoryForm;
